import LeadEvent, {LeadEventTypeEnum} from "./LeadEvent";
import * as moment from "moment";
import {LeadEventApiDto, LeadBDPChangeEventDataApiDto} from "../../../api/model/rest";
import {getUser} from "../../../store/view/user";
import AuthenticatedEntity from "../../user/AuthenticatedEntity";
import {option, Option} from "ts-option";
import {Icon} from "../../../hiberUiKit/src/atoms/Icon/Icon";

/**
 * Lead BDP changed event.
 */
export default class LeadBDPChangedEvent extends LeadEvent {
	/**
	 * @param {string} id identifier of event.
	 * @param {string} leadId lead id.
	 * @param {LeadEventTypeEnum} type type of the event.
	 * @param {AuthenticatedEntity} entity entity which creates the event.
	 * @param {moment.Moment} creationDate creation date of event.
	 * @param {string} referenceNumber lead reference number.
	 * @param {LeadBDPChangedEventData} data lead BDP change event data.
	 */
	constructor(public readonly id: string,
		public readonly leadId: string,
		public readonly type: LeadEventTypeEnum,
		public readonly entity: AuthenticatedEntity,
		public readonly creationDate: moment.Moment,
		public readonly referenceNumber: string,
		public readonly data: LeadBDPChangedEventData) {
		super(id, leadId, type, entity, creationDate, referenceNumber);
	}

	public static fromJson(json: LeadEventApiDto): LeadBDPChangedEvent {
		const data = LeadBDPChangedEventData.fromJson(json.data as LeadBDPChangeEventDataApiDto);
		const entity = AuthenticatedEntity.fromJson(json.entity);
		return new LeadBDPChangedEvent(json.id, json.leadId, LeadEventTypeEnum.BdpAssigneeChanged, entity,
			moment(json.createdAt), json.referenceNumber, data);
	}

	getIcon() {
		return Icon.User;
	}

	getActivityMessage(): string {
		return `Business Development Partner changed from ${this.getPreviousDisplayName()} to ${this.getCurrentDisplayName()}`
	}

	/**
	 * @return {string} display name of previous BDP.
	 */
	getPreviousDisplayName(): string {
		return this.data.previousBusinessDevelopmentPartnerId
			.map(id => getUser(id)
				.map(u => u.getDisplayName())
				.getOrElse("Unassigned")
			).getOrElse("Unassigned");
	}

	/**
	 * @return {string} display name of current BDP.
	 */
	getCurrentDisplayName(): string {
		return this.data.currentBusinessDevelopmentPartnerId
			.map(id => getUser(id)
				.map(u => u.getDisplayName())
				.getOrElse("Unassigned")
			).getOrElse("Unassigned");
	}
}

/**
 * Data specific for business development partner change event.
 */
export class LeadBDPChangedEventData {
	/**
	 * @param {Option<string>} previousBusinessDevelopmentPartnerId id of the user being previously assigned as BDP.
	 * @param {Option<string>} currentBusinessDevelopmentPartnerId id of the user being currently assigned as BDP.
	 */
	constructor(public readonly previousBusinessDevelopmentPartnerId: Option<string>,
		public readonly currentBusinessDevelopmentPartnerId: Option<string>) {}

	public static fromJson(json: LeadBDPChangeEventDataApiDto): LeadBDPChangedEventData {
		return new LeadBDPChangedEventData(option(json.previousBusinessDevelopmentPartnerId),
			option(json.currentBusinessDevelopmentPartnerId));
	}
}